import { TransactionListFooterComponent } from "./transaction-list-footer.component";
import { AfterViewInit, Component, inject, Input } from "@angular/core";
import { Router } from "@angular/router";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { CryptoWallet } from "@models";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { CryptoWalletService } from "@main-services/wallet/crypto_wallet.service";
import { MetalTransaction, SendTransaction, TradableTransaction } from "@models/transaction";
import { AccountService } from "@modules/account/services/account.service";
import { TooltipService } from "@main-services/tooltip.service";
import { FiatService } from "@main-services/currency";
import { ContactService } from "@main-services/contact.service";
import { PayAccountService } from "@main-services/pay_account.service";
import { UserService } from "@main-services/user.service";
import { ModalService } from "@core-services/modal.service";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { SendEntryPointService } from "@modules/+new/payments/features/send/domain/send-entry-point.service";
import { WalletBalance } from "@modules/+new/core/wallets-balances/domain/entities/wallet-balance.model";
import { UserFiatFundsService } from "@modules/shared/services/user-fiat-funds.service";
import { FiatDepositEntryService } from "@modules/+new/payments/features/fiat-deposit/infrastructure/fiat-deposit-entry.service";
import { LaunchDarklyService } from "@core-services/launch-darkly/launch-darkly.service";
import { CheckoutEntryHelperService } from "@modules/checkout/services/checkout-entry-helper.service";
import { OrderFlow } from "@modules/limit-orders/interfaces/limit-order-rudderstack.model";
import { LimitOrdersTrackingFacade } from "@modules/limit-orders/services/orders-rudderstack.facade";
import { isDigiAsset } from "@modules/trade/helpers/helper";

@Component({
	selector: "asset-transaction-list-footer",
	templateUrl: "./transaction-list-footer-template.component.html",
	styleUrls: ["./transaction-list-footer-template.component.scss"],
	standalone: false
})
export class AssetTransactionListFooterComponent extends TransactionListFooterComponent implements AfterViewInit {
	@Input()
	public transaction: TradableTransaction;
	@Input()
	public balance: WalletBalance = null;

	private cryptoWalletsForTransfer: boolean = false;
	private readonly router = inject(Router);

	public isDigiAsset: boolean = false;

	public constructor(
		protected modalService: ModalService,
		protected tradableWalletListService: TradableWalletListService,
		protected tradableListService: TradableListService,
		protected accountService: AccountService,
		protected fiatWalletService: FiatWalletService,
		protected fiatService: FiatService,
		protected tooltipService: TooltipService,
		protected userService: UserService,
		protected contactService: ContactService,
		protected payaccountService: PayAccountService,
		protected infoService: InfoService,
		protected translationService: TranslationService,
		private cryptoWalletService: CryptoWalletService,
		protected sendEntryPoint: SendEntryPointService,
		protected userFiatFundsService: UserFiatFundsService,
		protected readonly fiatDepositEntryService: FiatDepositEntryService,
		protected readonly launchDarklyService: LaunchDarklyService,
		protected readonly checkoutEntryHelper: CheckoutEntryHelperService,
		protected readonly limitOrdersTrackingFacade: LimitOrdersTrackingFacade
	) {
		super(
			modalService,
			tradableWalletListService,
			tradableListService,
			fiatWalletService,
			fiatService,
			tooltipService,
			userService,
			contactService,
			payaccountService,
			infoService,
			userFiatFundsService,
			fiatDepositEntryService,
			launchDarklyService,
			checkoutEntryHelper
		);
	}

	public getTradeType(): string {
		if (this.transaction instanceof MetalTransaction && this.transaction.isStorageFee) {
			return "";
		}
		return super.getTradeType();
	}

	public ngAfterViewInit(): void {
		this.isDigiAsset = isDigiAsset(this.tradable.getCurrencyType());
		super.ngAfterViewInit();
		this.cryptoWalletsForTransfer = true;

		if (!this.tradable.isWithdrawActive() || this.tradable.isMaintenance()) {
			this.cryptoWalletsForTransfer =
				this.tradableWalletListService.getWalletsByTradableId(this.tradableWallet?.getCurrencyId())?.length >
					1 && this.tradable.isTransferActive();
		}
	}

	public tradeMore(useCase: string = "buy"): void {
		this.limitOrdersTrackingFacade.updateFlow(OrderFlow.TradeMoreHistory);
		super.tradeMore(useCase);
		if (this.disableActionButton(useCase)) {
			return;
		}

		if (useCase === "send" || useCase === "transfer" || useCase === "withdrawal") {
			const sendOffer = new SendTransaction();
			const cryptoWallet: CryptoWallet = this.cryptoWalletService.getItemById(
				this.getTradableWalletWithEnoughFunds().getCurrencyId()
			);
			sendOffer.setWallet(cryptoWallet);
			sendOffer.setDirectTradableWalletOffer(true);
			this.sendEntryPoint.openCryptoSendModal({ transaction: sendOffer });
		}
	}

	public showFooterButtonWithdrawOrTradable(): boolean {
		if (this.router.url.includes("iframe")) {
			return false;
		}

		if (this.isOldGiveaway()) {
			return false;
		}

		if (this.getTradeType() === "withdrawal") {
			return false;
		}

		return (
			this.showButton() &&
			!["swap", "buy", "sell", "refund", "deposit"].some((tradeType) => this.getTradeType() === tradeType) &&
			!this.isOutgoing()
		);
	}

	public showFooterButtonNOTWithdrawOrTradable(): boolean {
		if (this.router.url.includes("iframe")) {
			return false;
		}

		if (this.isOldGiveaway()) {
			return false;
		}

		return (
			this.showButton() &&
			["buy", "sell", "swap"].some((tradeType) => this.getTradeType() === tradeType) &&
			!this.transaction.getTrade().isVoucher
		);
	}

	public disableActionButton(s: string): boolean {
		if (s === "send" || s === "transfer") {
			return this.lessThanMinSell() || !this.cryptoWalletsForTransfer || super.disableActionButton(s);
		}

		if (s === "withdrawal") {
			return this.balance.operationalBalance < +this.fiat.getMinWalletWithdraw();
		}

		return super.disableActionButton(s);
	}

	public getToolTip(action: string): string {
		if (
			(!this.tradable.isWithdrawActive() || this.tradable.isMaintenance()) &&
			this.tradable.isTransferActive() &&
			!this.cryptoWalletsForTransfer &&
			action === "send"
		) {
			return "action_send_inactive_tooltip";
		}
		return super.getToolTip(action);
	}
}
